import {
  useState, useMemo 
} from 'react';
import {
  useLocation, useNavigate 
} from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  useCatalogItems, AddRelatedItemModal 
} from '@/features/catalog';
import type {
  CatalogItem, RelatedItem 
} from '@/features/catalog';
import { toast } from 'sonner';
import { TABS } from './catalog-constants';
import { ItemsView } from './items-view';
import { CategoriesView } from './categories-view';

export function CatalogPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedItem, setSelectedItem] = useState<CatalogItem | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRelatedItemModal, setShowRelatedItemModal] = useState(false);
  const [relatedItemTarget, setRelatedItemTarget] = useState<CatalogItem | null>(null);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    category: '',
    description: '',
    unit: '',
    unitCost: '',
  });

  const activeTab = useMemo(() => {
    if (location.pathname.includes('/categories')) return 'categories';
    return 'items';
  }, [location.pathname]);

  const filters = useMemo(() => ({
    category: selectedCategory === 'All' ? undefined : selectedCategory,
    search: searchQuery || undefined,
  }), [selectedCategory, searchQuery]);

  const {
    data, isLoading, error, refetch, isFetching 
  } = useCatalogItems({ filters });
  const items = data?.data ?? [];

  const { data: allData } = useCatalogItems({});
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { All: 0 };
    for (const item of allData?.data ?? []) {
      counts.All++;
      counts[item.category] = (counts[item.category] ?? 0) + 1;
    }
    return counts;
  }, [allData]);

  const handleAddItem = () => {
    setFormData({
      name: '',
      sku: '',
      category: '',
      description: '',
      unit: '',
      unitCost: '' 
    });
    setEditingItem(null);
    setShowAddModal(true);
  };

  const handleEditItem = (item: CatalogItem) => {
    setFormData({
      name: item.name,
      sku: item.sku,
      category: item.category,
      description: item.description ?? '',
      unit: item.unit,
      unitCost: (item.unitCost ?? 0).toString(),
    });
    setEditingItem(item.itemId);
    setShowAddModal(true);
  };

  const handleSaveItem = () => {
    if (!formData.name || !formData.sku || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }
    toast.success(editingItem ? `Updated ${formData.name}` : `Created ${formData.name}`);
    setShowAddModal(false);
    refetch();
  };

  const handleAddSubstitute = (item: CatalogItem) => {
    setRelatedItemTarget(item);
    setShowRelatedItemModal(true);
  };

  const handleAddRelatedItem = (item: CatalogItem) => {
    setRelatedItemTarget(item);
    setShowRelatedItemModal(true);
  };

  const handleSaveRelatedItem = (relatedItem: RelatedItem) => {
    if (relatedItemTarget) {
      toast.success(`Added ${relatedItem.name} as ${relatedItem.relationshipType} for ${relatedItemTarget.name}`);
      // In a real app, this would call an API to save the relationship
      refetch();
    }
  };

  const handleUploadFile = (item: CatalogItem) => {
    toast.info(`Upload file for ${item.name} - Feature coming soon`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Master Catalog</h1>
          <p className="text-muted-foreground">Browse and manage your ingredient catalog</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="border-b">
        <nav className="flex gap-4">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => navigate(tab.href)}
                className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === 'items' && (
        <ItemsView
          items={items}
          isLoading={isLoading}
          error={error}
          categoryCounts={categoryCounts}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
          showAddModal={showAddModal}
          setShowAddModal={setShowAddModal}
          editingItem={editingItem}
          formData={formData}
          setFormData={setFormData}
          onAddItem={handleAddItem}
          onEditItem={handleEditItem}
          onSaveItem={handleSaveItem}
          onRefetch={refetch}
          onAddSubstitute={handleAddSubstitute}
          onAddRelatedItem={handleAddRelatedItem}
          onUploadFile={handleUploadFile}
        />
      )}

      {activeTab === 'categories' && (
        <CategoriesView categoryCounts={categoryCounts} setSelectedCategory={setSelectedCategory} />
      )}

      {/* Add Related Item Modal */}
      {relatedItemTarget && (
        <AddRelatedItemModal
          isOpen={showRelatedItemModal}
          onClose={() => {
            setShowRelatedItemModal(false);
            setRelatedItemTarget(null);
          }}
          onSave={handleSaveRelatedItem}
          currentItem={relatedItemTarget}
          catalogItems={allData?.data ?? []}
          existingRelatedIds={relatedItemTarget.relatedItems?.map((r) => r.itemId) ?? []}
        />
      )}
    </div>
  );
}
